import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listDatasets, updateDatasetStatus, deleteDataset as deleteDatasetAPI } from '../services/api/datasetService';
import { rebuildCollection } from '../services/api/collectionRebuildService';
import DatasetList from './DatasetList';
import CreateDatasetDialog from './CreateDatasetDialog';
import EditDatasetDialog from './EditDatasetDialog';
import ImportDatasetDialog from './ImportDatasetDialog';
import DatasetDetailsDialog from './DatasetDetailsDialog';

/**
 * DatasetManagement - Admin interface for managing datasets
 *
 * Features:
 * - List all datasets with status, person count, last import
 * - Create new datasets
 * - Import data files (JSON)
 * - Activate/deactivate datasets
 * - View import history and details
 */
export default function DatasetManagement() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusConfirmText, setStatusConfirmText] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Load datasets on mount
  useEffect(() => {
    loadDatasets();
  }, []);

  async function loadDatasets() {
    try {
      setLoading(true);
      setError(null);
      const data = await listDatasets();
      setDatasets(data);
    } catch (err) {
      console.error('Failed to load datasets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleDatasetCreated(newDataset) {
    showSnackbar(`Dataset "${newDataset.dataset_name}" created successfully`, 'success');
    loadDatasets();
  }

  function handleEditClick(dataset) {
    setSelectedDataset(dataset);
    setEditDialogOpen(true);
  }

  function handleDatasetUpdated(updatedDataset) {
    showSnackbar(`Dataset "${updatedDataset.dataset_name}" updated successfully`, 'success');
    setEditDialogOpen(false);
    loadDatasets();
  }

  function handleImportClick(dataset) {
    setSelectedDataset(dataset);
    setImportDialogOpen(true);
  }

  function handleImported({ importId, dataset }) {
    showSnackbar(`Import initiated for "${dataset.dataset_name}"`, 'success');
    loadDatasets();
  }

  function handleViewDetails(dataset) {
    setSelectedDataset(dataset);
    setDetailsDialogOpen(true);
  }

  function handleToggleStatus(dataset) {
    setSelectedDataset(dataset);
    setStatusConfirmText('');
    setStatusDialogOpen(true);
  }

  async function handleStatusConfirm() {
    if (!selectedDataset) return;
    if (statusConfirmText !== selectedDataset.dataset_id) {
      showSnackbar('Dataset ID does not match', 'error');
      return;
    }

    const newStatus = selectedDataset.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    try {
      await updateDatasetStatus(selectedDataset.dataset_id, newStatus);
      showSnackbar(
        `Dataset "${selectedDataset.dataset_name}" ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`,
        'success'
      );
      setStatusDialogOpen(false);
      setStatusConfirmText('');
      setSelectedDataset(null);
      loadDatasets();
    } catch (err) {
      showSnackbar(err.message || 'Failed to update dataset status', 'error');
    }
  }

  function handleDeleteClick(dataset) {
    setSelectedDataset(dataset);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  }

  async function handleDeleteConfirm() {
    if (!selectedDataset) return;
    if (deleteConfirmText !== selectedDataset.dataset_id) {
      showSnackbar('Dataset ID does not match', 'error');
      return;
    }

    try {
      await deleteDatasetAPI(selectedDataset.dataset_id);
      showSnackbar(`Dataset "${selectedDataset.dataset_name}" deleted`, 'success');
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      setSelectedDataset(null);
      loadDatasets();
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete dataset', 'error');
    }
  }

  async function handleRebuild(dataset) {
    try {
      showSnackbar(`Rebuilding collection for "${dataset.dataset_name}"...`, 'info');
      await rebuildCollection(dataset.dataset_id);
      showSnackbar(`Collection rebuild started for "${dataset.dataset_name}". This may take 10-15 minutes.`, 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to start collection rebuild', 'error');
    }
  }

  function showSnackbar(message, severity = 'info') {
    setSnackbar({ open: true, message, severity });
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Dataset Management
          </Typography>
          {!loading && datasets.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ px: 3 }}
        >
          New Dataset
        </Button>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && datasets.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50'
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No datasets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first dataset to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Dataset
          </Button>
        </Box>
      )}

      {/* Dataset List */}
      {!loading && datasets.length > 0 && (
        <DatasetList
          datasets={datasets}
          onEdit={handleEditClick}
          onImport={handleImportClick}
          onRebuild={handleRebuild}
          onViewDetails={handleViewDetails}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleStatus}
        />
      )}

      {/* Create Dataset Dialog */}
      <CreateDatasetDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleDatasetCreated}
      />

      {/* Edit Dataset Dialog */}
      <EditDatasetDialog
        open={editDialogOpen}
        dataset={selectedDataset}
        onClose={() => setEditDialogOpen(false)}
        onUpdated={handleDatasetUpdated}
      />

      {/* Import Dataset Dialog */}
      <ImportDatasetDialog
        open={importDialogOpen}
        dataset={selectedDataset}
        onClose={() => {
          setImportDialogOpen(false);
          setSelectedDataset(null);
        }}
        onImported={handleImported}
      />

      {/* Status Toggle Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedDataset?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} Dataset
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedDataset?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} dataset <strong>{selectedDataset?.dataset_name}</strong>?
            <br /><br />
            {selectedDataset?.status === 'ACTIVE' ? (
              <>This will make persons in this dataset unavailable for face matching and hide them from searches.</>
            ) : (
              <>This will make persons in this dataset available for face matching and visible in searches.</>
            )}
            <br /><br />
            To confirm, type the dataset ID: <strong>{selectedDataset?.dataset_id}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={selectedDataset?.dataset_id}
            value={statusConfirmText}
            onChange={(e) => setStatusConfirmText(e.target.value.toUpperCase())}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleStatusConfirm}
            color={selectedDataset?.status === 'ACTIVE' ? 'warning' : 'success'}
            variant="contained"
            disabled={statusConfirmText !== selectedDataset?.dataset_id}
          >
            {selectedDataset?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Dataset</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Delete <strong>{selectedDataset?.dataset_name}</strong>?
            <br /><br />
            This will permanently remove all {selectedDataset?.person_count || 0} person{selectedDataset?.person_count !== 1 ? 's' : ''} and
            import history. This action cannot be undone.
            <br /><br />
            To confirm, type the dataset ID: <strong>{selectedDataset?.dataset_id}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={selectedDataset?.dataset_id}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== selectedDataset?.dataset_id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dataset Details Dialog */}
      <DatasetDetailsDialog
        open={detailsDialogOpen}
        dataset={selectedDataset}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedDataset(null);
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
